// Q-Net 자격증 데이터 처리 유틸리티
let allQualifications = null;

// 비동기로 JSON 데이터 로드
const loadQualifications = async () => {
  if (!allQualifications) {
    try {
      const response = await fetch('/qnet-all-qualifications.json');
      if (!response.ok) {
        throw new Error('Failed to fetch qualifications');
      }
      allQualifications = await response.json();
    } catch (error) {
      console.error('Failed to load qualifications:', error);
      allQualifications = []; // 빈 배열로 폴백
    }
  }
  return allQualifications;
};

/**
 * 자격증명을 URL 슬러그로 변환
 */
export const createSlugFromName = (name) => {
  return name
    .toLowerCase()
    .replace(/[^\w\s가-힣-]/g, '') // 특수문자 제거
    .replace(/\s+/g, '-') // 공백을 하이픈으로
    .replace(/--+/g, '-') // 연속 하이픈 제거
    .trim();
};

/**
 * 자격증 레벨 분류
 */
export const getCertificationLevel = (seriescd, seriesnm) => {
  const levelMap = {
    1: { level: 'expert', name: '기술사', priority: 1 },
    2: { level: 'engineer', name: '기사', priority: 2 },
    3: { level: 'industrial', name: '산업기사', priority: 3 },
    4: { level: 'technician', name: '기능사', priority: 4 },
    5: { level: 'craftsman', name: '기능장', priority: 2 },
  };
  
  return levelMap[seriescd] || { level: 'other', name: seriesnm || '기타', priority: 5 };
};

/**
 * 자격증 분야 분류
 */
export const getCertificationCategory = (obligfldcd, obligfldnm) => {
  const categoryMap = {
    14: { key: 'construction', name: '건설', icon: '🏗️', color: 'bg-orange-100 text-orange-700' },
    15: { key: 'chemistry', name: '화학', icon: '🧪', color: 'bg-purple-100 text-purple-700' },
    16: { key: 'mechanical', name: '기계', icon: '⚙️', color: 'bg-blue-100 text-blue-700' },
    17: { key: 'textile', name: '섬유', icon: '🧵', color: 'bg-pink-100 text-pink-700' },
    18: { key: 'electrical', name: '전기·전자', icon: '⚡', color: 'bg-yellow-100 text-yellow-700' },
    19: { key: 'information', name: '정보통신', icon: '💻', color: 'bg-indigo-100 text-indigo-700' },
    20: { key: 'food', name: '식품가공', icon: '🍎', color: 'bg-green-100 text-green-700' },
    21: { key: 'printing', name: '인쇄·목재·가구·공예', icon: '📰', color: 'bg-amber-100 text-amber-700' },
    22: { key: 'beauty', name: '이용·숙박·여행·오락·스포츠', icon: '✂️', color: 'bg-rose-100 text-rose-700' },
    23: { key: 'transport', name: '운전·운송', icon: '🚛', color: 'bg-gray-100 text-gray-700' },
    24: { key: 'environment', name: '환경·에너지', icon: '🌱', color: 'bg-emerald-100 text-emerald-700' },
    25: { key: 'safety', name: '안전관리', icon: '🛡️', color: 'bg-red-100 text-red-700' },
    26: { key: 'agriculture', name: '농림어업', icon: '🌾', color: 'bg-lime-100 text-lime-700' }
  };

  return categoryMap[obligfldcd] || {
    key: 'other',
    name: obligfldnm || '기타',
    icon: '📋',
    color: 'bg-gray-100 text-gray-700'
  };
};

/**
 * CBT 지원 여부 확인 (기존 18개 자격증)
 */
const CBT_SUPPORTED_CODES = [
  1150, // 전기기사
  2140, // 전기산업기사
  7780, // 전기기능사
  1900, // 소방설비기사(기계분야)
  1431, // 산업안전기사
  1320, // 정보처리기사
  1630, // 건축기사
  7875, // 지게차운전기능사
  2670, // 컴퓨터활용능력1급
  2671, // 컴퓨터활용능력2급
  2590, // 공조냉동기계산업기사
  1441, // 산업위생관리기사
  1910, // 소방설비기사(전기분야)
  7910, // 한식조리기능사
];

const checkHasCBT = (jmcd) => {
  return CBT_SUPPORTED_CODES.includes(jmcd);
};

/**
 * 슬러그로 Q-Net 자격증 찾기 (개별 파일 우선, 폴백 시 기존 방식)
 */
export const getQNetCertificationBySlug = async (slug) => {
  try {
    // 1차: 개별 파일에서 직접 로드 시도 (99% 성능 향상)
    const individualCert = await getQNetCertificationBySlugDirect(slug);
    if (individualCert) {
      console.log(`🚀 개별 파일 로드 성공: ${slug}`);
      return individualCert;
    }
  } catch (error) {
    console.warn(`⚠️ 개별 파일 로드 실패: ${slug}, 기존 방식으로 폴백`, error.message);
  }
  
  // 2차: 기존 방식으로 폴백
  console.log(`🔄 기존 방식으로 폴백: ${slug}`);
  const qualifications = await loadQualifications();
  return qualifications.find(cert => {
    const certSlug = createSlugFromName(cert.jmfldnm);
    return certSlug === slug || cert.jmfldnm === slug;
  });
};

/**
 * 개별 파일에서 자격증 정보 직접 로드
 * @param {string} slug - 자격증 슬러그 또는 이름
 * @returns {Promise<object|null>} - 자격증 정보 또는 null
 */
export const getQNetCertificationBySlugDirect = async (slug) => {
  try {
    // 슬러그 정규화
    const normalizedSlug = slug.toLowerCase()
      .replace(/[^\w\s가-힣-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/--+/g, '-')
      .trim();
    
    // URL 디코딩 먼저 수행
    const decodedSlug = decodeURIComponent(slug);
    
    // 개별 파일 경로 시도
    const possiblePaths = [
      `/certs/${decodedSlug}.json`,       // URL 디코딩된 원본
      `/certs/${slug}.json`,              // 원본 그대로
      `/certs/${normalizedSlug}.json`,    // 정규화된 슬러그
    ];
    
    for (const path of possiblePaths) {
      try {
        const response = await fetch(path, {
          cache: 'force-cache', // 브라우저 캐시 활용
          headers: {
            'Accept': 'application/json'
          }
        });
        
        if (response.ok) {
          const certData = await response.json();
          
          // 개별 파일 데이터를 기존 형식으로 변환
          return {
            jmcd: certData.jmcd,
            jmfldnm: certData.jmfldnm,
            engfldnm: certData.engfldnm || '',
            seriescd: certData.seriescd,
            seriesnm: certData.seriesnm,
            obligfldcd: certData.obligfldcd,
            obligfldnm: certData.obligfldnm,
            mdobligfldcd: certData.mdobligfldcd,
            mdobligfldnm: certData.mdobligfldnm,
            // 추가 메타데이터
            _source: 'individual-file',
            _fileName: path,
            _generatedAt: certData.generatedAt
          };
        }
      } catch (fetchError) {
        // 다음 경로 시도
        continue;
      }
    }
    
    // 인덱스 파일을 통한 검색
    return await getQNetCertificationByIndex(slug);
    
  } catch (error) {
    console.warn('개별 파일 로드 실패:', error);
    return null;
  }
};

/**
 * 인덱스 파일을 통한 자격증 검색
 * @param {string} slug - 자격증 슬러그
 * @returns {Promise<object|null>} - 자격증 정보
 */
export const getQNetCertificationByIndex = async (slug) => {
  try {
    // 인덱스 파일에서 검색
    const indexResponse = await fetch('/certs/index.json', {
      cache: 'force-cache'
    });
    
    if (!indexResponse.ok) {
      throw new Error('인덱스 파일 로드 실패');
    }
    
    const indexData = await indexResponse.json();
    const decodedSlug = decodeURIComponent(slug);
    const certInfo = indexData.index[decodedSlug] || 
                     indexData.index[slug] ||
                     indexData.index[slug.toLowerCase()] ||
                     indexData.index[decodedSlug.toLowerCase()];
    
    if (certInfo && certInfo.fileName) {
      // 인덱스에서 찾은 파일명으로 개별 파일 로드
      const certResponse = await fetch(`/certs/${certInfo.fileName}`, {
        cache: 'force-cache'
      });
      
      if (certResponse.ok) {
        const certData = await certResponse.json();
        return {
          jmcd: certData.jmcd,
          jmfldnm: certData.jmfldnm,
          engfldnm: certData.engfldnm || '',
          seriescd: certData.seriescd,
          seriesnm: certData.seriesnm,
          obligfldcd: certData.obligfldcd,
          obligfldnm: certData.obligfldnm,
          mdobligfldcd: certData.mdobligfldcd,
          mdobligfldnm: certData.mdobligfldnm,
          _source: 'index-lookup',
          _fileName: certInfo.fileName,
          _generatedAt: certData.generatedAt
        };
      }
    }
    
    return null;
    
  } catch (error) {
    console.warn('인덱스 검색 실패:', error);
    return null;
  }
};

/**
 * Q-Net API에서 종목별 상세 정보 가져오기
 */
export const getQNetDetailedInfo = async (jmcd) => {
  if (!jmcd) return null;
  
  try {
    // Supabase Edge Function을 통한 HTTPS 프록시 호출
    const proxyUrl = `https://wvznaebdqkkqnxtxnsod.supabase.co/functions/v1/qnet-proxy`;
    
    const params = new URLSearchParams({
      jmCd: jmcd.toString().padStart(4, '0') // 4자리로 패딩
    });

    const response = await fetch(`${proxyUrl}?${params}`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Ind2em5hZWJkcWtrcW54dHhuc29kIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTU0NDQ2MTksImV4cCI6MjA3MTAyMDYxOX0.kkFkLfefIvWh3MCyLt4TpgFamQjLj2GsyKt6kDE2vYg'}`,
        'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Ind2em5hZWJkcWtrcW54dHhuc29kIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTU0NDQ2MTksImV4cCI6MjA3MTAyMDYxOX0.kkFkLfefIvWh3MCyLt4TpgFamQjLj2GsyKt6kDE2vYg'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Supabase Edge Function에서 파싱된 JSON 데이터 받기
    const jsonData = await response.json();
    
    // 에러 체크
    if (!jsonData.success) {
      console.warn('Q-Net API 응답 오류:', jsonData.error);
      return null;
    }
    
    // Edge Function에서 파싱된 데이터 사용
    const contents = jsonData.contents || '';
    const detailedInfo = parseQNetContents(contents);
    
    return {
      jmfldnm: jsonData.koreanName || '',
      obligfldcd: '',
      obligfldnm: '',
      mdobligfldcd: '',
      mdobligfldnm: '',
      contents: contents,
      parsedInfo: detailedInfo,
      eligibilityRequirements: jsonData.eligibilityRequirements || []
    };
  } catch (error) {
    console.error('Q-Net API 호출 실패:', error);
    return null;
  }
};

/**
 * Q-Net contents 텍스트에서 구조화된 정보 추출
 */
const parseQNetContents = (contents) => {
  if (!contents) return {};
  
  const info = {
    examOrganization: '',
    relatedMajors: '',
    testSubjects: '',
    testMethod: '',
    passingCriteria: ''
  };

  try {
    // 시행처 추출
    const orgMatch = contents.match(/①\s*시\s*행\s*처\s*:\s*([^②]+)/);
    if (orgMatch) {
      info.examOrganization = orgMatch[1].trim().replace(/\s+/g, ' ');
    }

    // 관련학과 추출
    const majorMatch = contents.match(/②\s*관련학과\s*:\s*([^③]+)/);
    if (majorMatch) {
      info.relatedMajors = majorMatch[1].trim().replace(/\s+/g, ' ');
    }

    // 시험과목 추출
    const subjectMatch = contents.match(/③\s*시험과목\s*:\s*([^④]+)/);
    if (subjectMatch) {
      info.testSubjects = subjectMatch[1].trim().replace(/\s+/g, ' ');
    }

    // 검정방법 추출
    const methodMatch = contents.match(/④\s*검정방법\s*[:\-\s]*([^⑤]+)/);
    if (methodMatch) {
      info.testMethod = methodMatch[1].trim().replace(/\s+/g, ' ');
    }

    // 합격기준 추출
    const criteriaMatch = contents.match(/⑤\s*합격기준\s*[:\-\s]*(.+)/);
    if (criteriaMatch) {
      info.passingCriteria = criteriaMatch[1].trim().replace(/\s+/g, ' ');
    }

  } catch (error) {
    console.error('Contents 파싱 오류:', error);
  }

  return info;
};

/**
 * Q-Net 자격증 데이터를 ExamDetailPage 형식으로 변환
 */
export const convertQNetToExamFormat = (qnetCert, skipDetailedInfo = false) => {
  if (!qnetCert) return null;
  
  const level = getCertificationLevel(qnetCert.seriescd, qnetCert.seriesnm);
  const category = getCertificationCategory(qnetCert.obligfldcd, qnetCert.obligfldnm);
  const hasCBT = checkHasCBT(qnetCert.jmcd);
  
  return {
    title: qnetCert.jmfldnm,
    description: `${category.name} 분야의 ${level.name} 자격증`,
    category: category.name,
    level: level.name,
    examCode: qnetCert.jmcd.toString(),
    hasCBT,
    hasAPIIntegration: hasCBT, // CBT 지원하는 자격증은 API도 연동
    qnetData: qnetCert,
    qnetDetails: {
      examOrganization: '한국산업인력공단',
      relatedMajors: '관련 전공학과 정보를 준비중입니다.',
      testMethod: '필기 + 실기시험',
      passingCriteria: '60점 이상'
    },
    // CBT가 없는 경우 기본값
    years: hasCBT ? undefined : {}, // CBT가 있으면 기존 examData에서 처리
    subjects: ['기본과목'],
    studyTips: {
      studyPeriod: '6개월',
      keyPoints: ['기본 이론 학습', '실무 경험 쌓기', '최신 동향 파악']
    },
    examInfo: {
      duration: 180,
      totalQuestions: 100,
      passingScore: 60,
      questionTypes: ['객관식']
    }
  };
};

export const convertQNetToExamFormatWithDetails = async (qnetCert) => {
  if (!qnetCert) return null;
  
  const level = getCertificationLevel(qnetCert.seriescd, qnetCert.seriesnm);
  const category = getCertificationCategory(qnetCert.obligfldcd, qnetCert.obligfldnm);
  const hasCBT = checkHasCBT(qnetCert.jmcd);
  
  // 상세 정보 가져오기 (개별 페이지에서만 호출)
  const detailedInfo = await getQNetDetailedInfo(qnetCert.jmcd);
  
  return {
    title: qnetCert.jmfldnm,
    description: `${category.name} 분야의 ${level.name} 자격증`,
    category: category.name,
    level: level.name,
    examCode: qnetCert.jmcd.toString(),
    hasCBT,
    hasAPIIntegration: hasCBT, // CBT 지원하는 자격증은 API도 연동
    qnetData: qnetCert,
    qnetDetails: detailedInfo?.parsedInfo || {
      examOrganization: '한국산업인력공단',
      relatedMajors: '관련 전공학과 정보를 준비중입니다.',
      testMethod: '필기 + 실기시험',
      passingCriteria: '60점 이상'
    },
    // CBT가 없는 경우 기본값
    years: hasCBT ? undefined : {}, // CBT가 있으면 기존 examData에서 처리
    subjects: detailedInfo?.parsedInfo?.testSubjects ? 
      detailedInfo.parsedInfo.testSubjects.split(',').map(s => s.trim()) : 
      ['기본과목'],
    studyTips: {
      studyPeriod: '6개월',
      keyPoints: ['기본 이론 학습', '실무 경험 쌓기', '최신 동향 파악']
    },
    examInfo: {
      duration: 180,
      totalQuestions: 100,
      passingScore: 60,
      questionTypes: ['객관식']
    }
  };
};

export default {
  loadQualifications,
  getQNetCertificationBySlug,
  convertQNetToExamFormat,
  convertQNetToExamFormatWithDetails,
  createSlugFromName,
  getCertificationLevel,
  getCertificationCategory
};